import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { getFiscalYearStartMonthSetting } from '../api/workspaceBranding';
import {
  DEFAULT_FISCAL_YEAR_START_MONTH,
  getFiscalYearStartMonth,
  setFiscalYearStartMonth,
} from '../utils/fiscalYear';

const FiscalYearContext = createContext(null);

const TOKEN_STORAGE_KEY = 'token';

const applyStartMonth = (month) => {
  const next = month || DEFAULT_FISCAL_YEAR_START_MONTH;
  setFiscalYearStartMonth(next);
  return next;
};

export function FiscalYearProvider({ children }) {
  const location = useLocation();
  const loadedForToken = useRef(null);
  const [startMonth, setStartMonth] = useState(getFiscalYearStartMonth);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);

  useEffect(() => {
    if (!token) {
      loadedForToken.current = null;
      setStartMonth(applyStartMonth(DEFAULT_FISCAL_YEAR_START_MONTH));
      setSessionLoaded(false);
      return undefined;
    }
    if (loadedForToken.current === token) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const month = await getFiscalYearStartMonthSetting();
        if (cancelled) return;
        setStartMonth(applyStartMonth(month));
        loadedForToken.current = token;
      } catch {
        return;
      } finally {
        if (!cancelled) setSessionLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, token]);

  const refresh = useCallback(async () => {
    const month = await getFiscalYearStartMonthSetting();
    setStartMonth(applyStartMonth(month));
  }, []);

  const value = useMemo(() => ({ startMonth, refresh }), [startMonth, refresh]);

  if (token && !sessionLoaded) return null;
  return <FiscalYearContext.Provider value={value}>{children}</FiscalYearContext.Provider>;
}

FiscalYearProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useFiscalYear() {
  return useContext(FiscalYearContext);
}
